import React, { useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  setIsAllEmployeesSelected,
  setIsRevieweesCustomViewsSettings,
  setReviewees,
  useIsRevieweesCustomViewsSettings,
  useIsReviewsOverlappingEmployees,
  useIsAllEmployeesSelected,
  useReviewCreationData,
  useReviewees,
  useTotalEmployeeCount,
} from 'reducers/reviewCreationSlice';
import { VIEWS_ACTIONS, setShowViewsPanel, setViewAction } from 'reducers/viewsSlice';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import { Peers } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { ListRadioWithContent } from 'uiToolkitV1/ListRadioWithContent';
import { RadioSelect } from 'uiToolkitV1/RadioSelect';
import { SearchableAgGridMenu } from 'uiToolkitV1/SearchableAgGridMenu/SearchableAgGridMenu';
import { TagColored } from 'uiToolkitV1/Tag';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useGetReviewViewsList, useGetReviewees, useSaveReviewees } from '../../Hooks/dataHooks/revieweesDataHooks';
import { ReviewCreationFooter } from '../../ReviewCreationFooter';
import { RevieweesOverlappingEmployees } from './RevieweesOverlappingEmployees';
import { ViewEmployeeList } from './RevieweesViewsEmployees';

const Footer = () => {
  const revieweesViews = useReviewees();
  const isRevieweesCustomViewsSettings = useIsRevieweesCustomViewsSettings();
  const isAllEmployeesSelected = useIsAllEmployeesSelected();
  const saveDataMutation = useSaveReviewees();

  const isReviewsOverlappingEmployees = useIsReviewsOverlappingEmployees();
  const isContinueDisabled =
    (!isAllEmployeesSelected && revieweesViews?.length === 0) ||
    (isRevieweesCustomViewsSettings && isReviewsOverlappingEmployees) ||
    saveDataMutation.isLoading;

  const handleContinue = () => {
    saveDataMutation.mutate();
  };

  return <ReviewCreationFooter isContinueDisabled={isContinueDisabled} onContinue={handleContinue} />;
};

const ViewsSelection = () => {
  const { data: views, isSuccess, isLoading } = useGetReviewViewsList();
  const reviewees = useReviewees();
  const dispatch = useDispatch();

  const viewsRowData = useMemo(
    () =>
      (views ?? []).map((view) => ({
        id: view.id,
        title: view.name,
        optionData: view,
      })),
    [views]
  );

  // this is required when views are deleted from manage views, so that they are not still selected in reviewees
  useEffect(() => {
    if (isLoading || !isSuccess) return;
    const validViews = reviewees.filter((reviewee) => views.some((view) => view.id === reviewee.id));
    if (validViews.length !== reviewees.length) {
      dispatch(setReviewees(validViews));
    }
  }, [dispatch, isLoading, isSuccess, reviewees, views]);

  const defaultCheckedRows = useMemo(
    () => viewsRowData.filter((row) => reviewees.some((reviewee) => reviewee.id === row.id)),
    [viewsRowData, reviewees]
  );

  const onViewsApplyHandler = (checkedRows) => {
    dispatch(setReviewees(checkedRows.map((row) => row.optionData)));
  };

  const createViewHandler = () => {
    dispatch(setViewAction(VIEWS_ACTIONS.CREATE));

    dispatch(
      setShowViewsPanel({
        // todo: vikalp review creation add to selected views for better ux
        // onSuccess: onCohortCreationSuccess({ source }),
        constraints: {
          disallowPrivate: true,
          source: 'REVIEW',
          allowOnlyMyCompany: true,
          disableApply: true,
        },
      })
    );
  };

  return (
    <>
      <Flex gap={6} alignItems="stretch" mt={2}>
        <Flex flexDir="column" flexGrow={1} justifyContent="space-between" maxW="500px">
          <Flex flexDir="column">
            <Flex alignItems="center">
              <TextBodyMedium mr={2}>Select your views: </TextBodyMedium>
              <TooltipInfoV1 title="You will only see views which are public and with the base of “My Company”" />
            </Flex>
            <SearchableAgGridMenu
              rowData={viewsRowData}
              onApply={onViewsApplyHandler}
              defaultCheckedRows={defaultCheckedRows}
              key={defaultCheckedRows.length}
              placeholder="Add Views"
            />
          </Flex>
        </Flex>
        <>
          <Box borderRight="1px solid" borderColor="border.EBEFF3" />

          <Flex flexDir="column" flexGrow={1} justifyContent="space-between">
            <Flex pos="relative" mt={1}>
              Or
              <Button variant="tertiary" position="absolute" top="-10px" left="8px" onClick={createViewHandler}>
                Create View
              </Button>
            </Flex>
            <TextLabel color="text.g2">
              Views will be created using only ‘My Company’ as the base and have visibility set to ‘Public’
            </TextLabel>
          </Flex>
        </>
      </Flex>
      <ViewEmployeeList />
    </>
  );
};

const CustomizeViewsSetting = () => {
  const isRevieweesCustomViewsSettings = useIsRevieweesCustomViewsSettings();

  const dispatch = useDispatch();

  const onChangeHandler = (index) => {
    dispatch(setIsRevieweesCustomViewsSettings(index === 0));
  };

  const reviewees = useReviewees();
  if (reviewees.length <= 1) {
    return null;
  }

  const options = [
    {
      name: 'Yes, customize',
    },
    {
      name: 'No, use same settings',
    },
  ];

  const selectedIndex = isRevieweesCustomViewsSettings ? 0 : 1;

  return (
    <>
      <Divider my={3} />
      <Box p="10px 12px">
        <Flex alignItems="center">
          <TextBodyMedium mr={2} mb="2px">
            Would you like different Views to be configured with different settings?
          </TextBodyMedium>
        </Flex>
        <TextLabel color="text.g2">
          Different settings are applicable to all settings except : Review Details, Calibration, and Validation
        </TextLabel>
        <RadioSelect options={options} selectedIndex={selectedIndex} onChange={onChangeHandler} />
      </Box>
    </>
  );
};

const Reviewees = () => {
  const { reviewCycleId } = useReviewCreationData();
  const reviewees = useReviewees();
  const isAllEmployeesSelected = useIsAllEmployeesSelected();
  const totalEmployeeCount = useTotalEmployeeCount();
  const { isSuccess, isLoading } = useGetReviewees({ reviewCycleId });
  const dispatch = useDispatch();
  const [selectedIndex, setSelectedIndex] = useState(null);

  const items = [
    {
      title: 'All Employees',
      // eslint-disable-next-line react/no-unstable-nested-components
      rightContentComp: () => {
        if (totalEmployeeCount === 0) {
          return null;
        }
        return (
          <TagColored color="black">
            {totalEmployeeCount} employee{addPluralSuffixS(totalEmployeeCount)}
          </TagColored>
        );
      },
    },
    {
      title: 'Include specific users for this review cycle by selecting Views',
      subtitle: 'Access Views from the dropdown below or create new views containing subsets of your employee base',
      content: <ViewsSelection />,
    },
  ];

  // this is better than useEffect to sync values, Dan Abromov said so
  if (isSuccess && selectedIndex === null) {
    if (isAllEmployeesSelected) {
      setSelectedIndex(0);
    } else if (reviewees.length >= 1) {
      setSelectedIndex(1);
    }
  }

  const onChange = (index) => {
    if (index === 0) {
      dispatch(setIsAllEmployeesSelected(true));
    } else {
      dispatch(setReviewees([]));
    }
    setSelectedIndex(index);
  };

  return (
    <>
      <ExpandablePaper showLoader={isLoading}>
        <ExpandablePaperHeader
          title="Select employees to be reviewed"
          leftIconComponent={<Peers boxSize="24px" color="icon.g2" />}
        />
        <ExpandablePaperBody>
          <ListRadioWithContent items={items} onChange={onChange} checkedIndex={selectedIndex} />
          <CustomizeViewsSetting />
          <RevieweesOverlappingEmployees />
        </ExpandablePaperBody>
      </ExpandablePaper>

      <Footer />
    </>
  );
};

export { Reviewees };
